/**
 * デバイス詳細ページ - melta UI
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { deviceApi, shadowApi, provisionApi, firmwareApi, otaApi } from '../services/api';
import { userApi } from '../services/userApi';
import { adminUserApi, organizationApi } from '../services/adminApi';
import { Icon } from '../components/icons';
import { Button, Card, Badge, Alert, Modal, Select } from '../components/ui';
import { Textarea } from '../components/ui/Input';
import FlightLogTable from '../components/FlightLogTable';

const STATUS_CONFIG = {
  active: { label: 'Active', variant: 'success' },
  registered: { label: 'Registered', variant: 'primary' },
  maintenance: { label: 'Maintenance', variant: 'warning' },
  retired: { label: 'Retired', variant: 'default' },
  unknown: { label: 'Unknown', variant: 'default' },
};

const TRANSITIONS = {
  registered: ['active', 'retired'],
  active: ['maintenance', 'retired'],
  maintenance: ['active', 'retired'],
  retired: [],
  unknown: ['registered', 'active'],
};

// 担当者の変更権限（backend/lambda/device-api/assignee.mjs の ASSIGNEE_POLICY と揃える）
// operator を外して admin のみにする場合はここと assignee.mjs の self を変更する
const ASSIGNEE_POLICY = {
  manage: ['superadmin', 'admin'],   // 任意のメンバーを設定・解除
  self: ['operator'],                // 自分自身のみ設定・解除
  crossOrg: ['superadmin'],          // 全組織のメンバーから選べる
};

export default function DeviceDetail() {
  const { thingName } = useParams();
  const navigate = useNavigate();

  const [device, setDevice] = useState(null);
  const [shadow, setShadow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shadowError, setShadowError] = useState(null);

  // Shadow編集
  const [showShadowEditor, setShowShadowEditor] = useState(false);
  const [shadowEditValue, setShadowEditValue] = useState('');
  const [shadowUpdating, setShadowUpdating] = useState(false);

  // ステータス変更
  const [statusChanging, setStatusChanging] = useState(false);

  // OTA更新
  const [showOtaModal, setShowOtaModal] = useState(false);
  const [firmwareList, setFirmwareList] = useState([]);
  const [firmwareLoading, setFirmwareLoading] = useState(false);
  const [selectedFirmware, setSelectedFirmware] = useState('');
  const [otaStarting, setOtaStarting] = useState(false);

  // 通信履歴
  // 担当者（NEX2-3244）
  const [profile, setProfile] = useState(null);
  const [showAssigneeModal, setShowAssigneeModal] = useState(false);
  const [assigneeCandidates, setAssigneeCandidates] = useState([]); // { userId, orgId, label }
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');      // `${orgId}|${userId}`
  const [assigneeSaving, setAssigneeSaving] = useState(false);

  const [commTab, setCommTab] = useState('events'); // events | mqtt | http
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [hasMoreEvents, setHasMoreEvents] = useState(false);
  const [eventFilter, setEventFilter] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const deviceData = await deviceApi.get(thingName);
      setDevice(deviceData);

      try {
        const shadowData = await shadowApi.get(thingName);
        setShadow(shadowData);
      } catch (err) {
        setShadowError(err.message?.includes('404') ? 'Shadow未作成' : err.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [thingName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 自分のロール（担当者の変更 UI の出し分けに使う）
  useEffect(() => {
    userApi.me().then(setProfile).catch(() => setProfile(null));
  }, []);

  // イベント履歴取得
  const fetchEvents = useCallback(async (offset = 0) => {
    setEventsLoading(true);
    try {
      const params = { limit: 15, offset };
      if (eventFilter !== 'all') params.eventType = eventFilter;
      const result = await deviceApi.listEvents(thingName, params);
      if (offset === 0) {
        setEvents(result.events || []);
      } else {
        setEvents(prev => [...prev, ...(result.events || [])]);
      }
      setHasMoreEvents(result.hasMore || false);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setEventsLoading(false);
    }
  }, [thingName, eventFilter]);

  // MQTTログ取得
  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const result = await deviceApi.listLogs(thingName, { limit: 50 });
      setLogs(result.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLogsLoading(false);
    }
  }, [thingName]);

  // タブ切替時にデータ取得
  useEffect(() => {
    if (commTab === 'events' && events.length === 0) {
      fetchEvents(0);
    } else if ((commTab === 'mqtt' || commTab === 'http') && logs.length === 0) {
      fetchLogs();
    }
  }, [commTab, events.length, logs.length, fetchEvents, fetchLogs]);

  // フィルター変更時にイベント再取得
  useEffect(() => {
    if (commTab === 'events') {
      fetchEvents(0);
    }
  }, [eventFilter, commTab, fetchEvents]);

  // Shadow更新
  const handleShadowUpdate = async () => {
    setShadowUpdating(true);
    try {
      const desired = JSON.parse(shadowEditValue);
      await shadowApi.update(thingName, desired);
      setShowShadowEditor(false);
      const shadowData = await shadowApi.get(thingName);
      setShadow(shadowData);
    } catch (err) {
      alert('Shadow更新エラー: ' + err.message);
    } finally {
      setShadowUpdating(false);
    }
  };

  // ライフサイクルステータス変更
  const handleStatusChange = async (newStatus) => {
    if (!window.confirm(`ステータスを "${newStatus}" に変更しますか？`)) return;
    setStatusChanging(true);
    try {
      await provisionApi.updateStatus(thingName, newStatus);
      await fetchData();
    } catch (err) {
      alert('ステータス変更エラー: ' + err.message);
    } finally {
      setStatusChanging(false);
    }
  };

  // 証明書ローテーション
  const handleRotateCert = async () => {
    if (!window.confirm('証明書をローテーションしますか？現在の証明書は無効化されます。')) return;
    try {
      const result = await provisionApi.rotateCert(thingName);
      alert('証明書が更新されました。新しい秘密鍵をダウンロードしてください。');
      downloadFile(`${thingName}-certificate.pem.crt`, result.newCertificatePem);
      downloadFile(`${thingName}-private.pem.key`, result.newPrivateKey);
      await fetchData();
    } catch (err) {
      alert('証明書ローテーションエラー: ' + err.message);
    }
  };

  // ---- 担当者 ----
  const role = profile?.role ?? null;
  const canManageAssignee = ASSIGNEE_POLICY.manage.includes(role);
  const canSelfAssign     = ASSIGNEE_POLICY.self.includes(role);
  const isCrossOrg        = ASSIGNEE_POLICY.crossOrg.includes(role);
  const isSelfAssigned    = !!profile && !!device?.assigneeUserId && device.assigneeUserId === profile.userId;
  // self ロールは「自分を担当にする」「（自分が担当なら）解除」だけ
  const canChangeAssignee = canManageAssignee || (canSelfAssign && (!device?.assigneeUserId || isSelfAssigned));

  const handleOpenAssigneeModal = async () => {
    setShowAssigneeModal(true);
    setSelectedAssignee(device?.assigneeUserId ? `${device.assigneeOrgId || profile?.orgId || ''}|${device.assigneeUserId}` : '');
    if (!canManageAssignee) return; // self ロールは候補一覧不要
    setAssigneeLoading(true);
    try {
      let candidates;
      if (isCrossOrg) {
        // superadmin: 全組織のメンバーを組織名付きで出す
        const [usersRes, orgsRes] = await Promise.all([
          adminUserApi.list({ limit: 200 }),
          organizationApi.list({ limit: 100 }).catch(() => ({ items: [] })),
        ]);
        const orgName = Object.fromEntries((orgsRes.items || []).map((o) => [o.orgId, o.orgName || o.name || o.orgId]));
        candidates = (usersRes.items || []).map((u) => ({
          userId: u.userId, orgId: u.orgId,
          label: `${u.displayName || u.email}${u.email && u.displayName ? ` (${u.email})` : ''} — ${orgName[u.orgId] || u.orgId}`,
        }));
      } else {
        const res = await userApi.list();
        candidates = (res.users || []).map((u) => ({
          userId: u.userId, orgId: u.orgId || profile?.orgId,
          label: `${u.displayName || u.email}${u.email && u.displayName ? ` (${u.email})` : ''}`,
        }));
      }
      candidates = candidates
        .filter((c) => c.userId)
        .sort((a, b) => a.label.localeCompare(b.label, 'ja'));
      setAssigneeCandidates(candidates);
    } catch (err) {
      alert('メンバー一覧の取得に失敗しました: ' + err.message);
      setShowAssigneeModal(false);
    } finally {
      setAssigneeLoading(false);
    }
  };

  const applyAssignee = async (userId, orgId) => {
    setAssigneeSaving(true);
    try {
      await deviceApi.setAssignee(thingName, userId, isCrossOrg && userId ? orgId : undefined);
      setShowAssigneeModal(false);
      await fetchData();
    } catch (err) {
      alert('担当者の変更に失敗しました: ' + err.message);
    } finally {
      setAssigneeSaving(false);
    }
  };

  const handleSaveAssignee = () => {
    if (!selectedAssignee) return;
    const [orgId, userId] = selectedAssignee.split('|');
    applyAssignee(userId, orgId);
  };

  const handleClearAssignee = () => {
    if (!window.confirm('担当者を解除しますか？')) return;
    applyAssignee(null);
  };

  // OTA更新モーダルを開く（承認済み=released のファームウェア一覧を取得）
  const handleOpenOtaModal = async () => {
    setShowOtaModal(true);
    setSelectedFirmware('');
    setFirmwareLoading(true);
    try {
      const result = await firmwareApi.list();
      const released = (result.firmware || []).filter((fw) => fw.status === 'released');
      setFirmwareList(released);
    } catch (err) {
      alert('ファームウェア一覧の取得に失敗しました: ' + err.message);
      setShowOtaModal(false);
    } finally {
      setFirmwareLoading(false);
    }
  };

  // OTAジョブ作成（このデバイスのみを対象）
  const handleStartOta = async () => {
    if (!selectedFirmware) {
      alert('配信するファームウェアを選択してください');
      return;
    }
    const firmware = firmwareList.find((fw) => fw.version === selectedFirmware);
    const updateTypeLabel = firmware?.updateType === 'rauc-update'
      ? 'RAUCバンドル（OS全体・A/Bパーティション更新、完了後に自動再起動）'
      : 'ota-agent自己更新';
    if (!window.confirm(
      `${thingName} へ OTA 更新を実行しますか？\n\n` +
      `バージョン: ${selectedFirmware}\n` +
      `更新方式: ${updateTypeLabel}`
    )) {
      return;
    }

    setOtaStarting(true);
    try {
      const thingArn = device.thingArn || `arn:aws:iot:ap-northeast-1:996561439165:thing/${thingName}`;
      const result = await otaApi.create({
        firmwareVersion: selectedFirmware,
        targets: [thingArn],
        targetSelection: 'SNAPSHOT',
        description: `Device OTA: ${thingName} -> ${selectedFirmware}`,
      });
      setShowOtaModal(false);
      navigate(`/ota/${result.jobId}`);
    } catch (err) {
      alert('OTAジョブ作成エラー: ' + err.message);
    } finally {
      setOtaStarting(false);
    }
  };

  // デバイス削除
  const handleDelete = async () => {
    if (!window.confirm(`"${thingName}" を削除しますか？この操作は取り消せません。`)) return;
    try {
      await deviceApi.delete(thingName);
      navigate('/devices');
    } catch (err) {
      alert('削除エラー: ' + err.message);
    }
  };

  const downloadFile = (filename, content) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        {error}
        <Button variant="secondary" size="sm" onClick={fetchData} className="ml-4">
          再試行
        </Button>
      </Alert>
    );
  }

  if (!device) return null;

  const status = STATUS_CONFIG[device.lifecycleStatus] || STATUS_CONFIG.unknown;
  const allowedTransitions = TRANSITIONS[device.lifecycleStatus] || [];

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/devices')}>
          <Icon name="chevronLeft" className="w-4 h-4" />
          戻る
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900">{thingName}</h1>
            <Badge variant={status.variant} size="md">
              {status.label}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" iconOnly onClick={fetchData} aria-label="更新">
            <Icon name="refresh" className="w-5 h-5" />
          </Button>
          <Button variant="primary" onClick={handleOpenOtaModal}>
            <Icon name="uploadCloud" className="w-4 h-4" />
            OTA更新
          </Button>
          <Button variant="danger-outline" onClick={handleDelete}>
            <Icon name="trash" className="w-4 h-4" />
            削除
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* 基本情報 */}
        <Card title="基本情報">
          <div className="space-y-4">
            <InfoRow label="機体名" value={device.thingName} />
            <InfoRow label="機体タイプ" value={device.thingTypeName || <span className="text-slate-400">未設定</span>} />
            <InfoRow
              label="グループ"
              value={device.groups?.length > 0
                ? device.groups.map(g => g.groupName).join(', ')
                : <span className="text-slate-400">なし</span>
              }
            />
            <InfoRow
              label="担当者"
              value={
                <div className="flex items-center gap-3 flex-wrap">
                  {device.assigneeUserId ? (
                    <span title={device.assigneeEmail || undefined}>
                      {device.assigneeDisplayName || device.assigneeEmail}
                      {device.assigneeDisplayName && device.assigneeEmail && (
                        <span className="text-slate-400 ml-1 text-xs">({device.assigneeEmail})</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-slate-400">未割り当て</span>
                  )}
                  {canChangeAssignee && (
                    <Button variant="ghost" size="sm" onClick={handleOpenAssigneeModal}>
                      <Icon name="edit" className="w-4 h-4" />
                      変更
                    </Button>
                  )}
                </div>
              }
            />
            <InfoRow
              label="登録者"
              value={device.registeredBy || <span className="text-slate-400">—</span>}
            />
            <InfoRow
              label="登録日時"
              value={device.registeredAt
                ? new Date(device.registeredAt).toLocaleString('ja-JP')
                : '—'
              }
            />
            <InfoRow
              label="最終接続"
              value={device.lastConnectedAt
                ? new Date(device.lastConnectedAt).toLocaleString('ja-JP')
                : '—'
              }
            />
            <InfoRow label="FW Version" value={device.firmwareVersion || '—'} />
          </div>

          {/* OTA情報 */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h4 className="text-sm font-medium text-slate-700 mb-4">OTA情報</h4>
            <div className="space-y-3">
              <InfoRow
                label="適用FWバージョン"
                value={shadow?.state?.reported?.osVersion || '—'}
              />
              <InfoRow
                label="稼働スロット"
                value={shadow?.state?.reported?.bootSlot ? (
                  <Badge variant="primary" size="sm">
                    {shadow.state.reported.bootSlot}面
                  </Badge>
                ) : '—'}
              />
              <InfoRow
                label="RAUC状態"
                value={shadow?.state?.reported?.raucStatus ? (
                  <Badge
                    variant={
                      shadow.state.reported.raucStatus === 'good'
                        ? 'success'
                        : shadow.state.reported.raucStatus === 'bad'
                          ? 'danger'
                          : 'default'
                    }
                    size="sm"
                  >
                    {shadow.state.reported.raucStatus === 'good'
                      ? '正常'
                      : shadow.state.reported.raucStatus === 'bad'
                        ? '異常'
                        : shadow.state.reported.raucStatus}
                  </Badge>
                ) : '—'}
              />
              <InfoRow
                label="Agentバージョン"
                value={shadow?.state?.reported?.version || '—'}
              />
              <InfoRow
                label="最終再起動"
                value={shadow?.state?.reported?.lastRebootAt
                  ? new Date(shadow.state.reported.lastRebootAt).toLocaleString('ja-JP')
                  : '—'
                }
              />
            </div>
          </div>

          {/* 属性 */}
          {device.attributes && Object.keys(device.attributes).length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h4 className="text-sm font-medium text-slate-700 mb-4">属性 (Attributes)</h4>
              <div className="space-y-3">
                {Object.entries(device.attributes).map(([key, val]) => (
                  <InfoRow key={key} label={key} value={val} />
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* ライフサイクル操作 */}
        <Card title="ライフサイクル管理">
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">現在のステータス</span>
              <Badge variant={status.variant} size="md">
                {status.label}
              </Badge>
            </div>

            {allowedTransitions.length > 0 && (
              <div>
                <p className="text-sm text-slate-600 mb-3">ステータスを変更:</p>
                <div className="flex flex-wrap gap-2">
                  {allowedTransitions.map((t) => {
                    const tConfig = STATUS_CONFIG[t];
                    return (
                      <Button
                        key={t}
                        variant="secondary"
                        size="sm"
                        onClick={() => handleStatusChange(t)}
                        disabled={statusChanging}
                      >
                        <Icon name="arrowRight" className="w-4 h-4" />
                        {tConfig.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="pt-6 border-t border-slate-200">
              <h4 className="text-sm font-medium text-slate-700 mb-4">証明書</h4>
              <div className="text-xs text-slate-500 break-all mb-4">
                {device.certificateArn || '—'}
              </div>
              <Button variant="secondary" size="sm" onClick={handleRotateCert}>
                <Icon name="refresh" className="w-4 h-4" />
                証明書ローテーション
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Device Shadow */}
      <Card
        title="Device Shadow"
        headerActions={
          <Button
            size="sm"
            onClick={() => {
              setShadowEditValue(
                JSON.stringify(shadow?.state?.desired || {}, null, 2)
              );
              setShowShadowEditor(true);
            }}
          >
            <Icon name="edit" className="w-4 h-4" />
            Desired を更新
          </Button>
        }
      >
        {shadowError ? (
          <div className="text-center py-8 text-slate-500">{shadowError}</div>
        ) : shadow ? (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Reported</h4>
              <pre className="text-xs bg-white rounded border border-slate-200 p-3 overflow-auto max-h-64">
                {JSON.stringify(shadow.state?.reported || {}, null, 2)}
              </pre>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Desired</h4>
              <pre className="text-xs bg-white rounded border border-slate-200 p-3 overflow-auto max-h-64">
                {JSON.stringify(shadow.state?.desired || {}, null, 2)}
              </pre>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">差分</h4>
              {shadow.diff && shadow.diff.length > 0 ? (
                <div className="space-y-2">
                  {shadow.diff.map((d, i) => (
                    <div key={i} className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                      <div className="font-medium text-amber-800">{d.path}</div>
                      <div className="text-amber-600 mt-1">
                        <span className="line-through">{JSON.stringify(d.reported)}</span>
                        {' → '}
                        <span className="font-medium">{JSON.stringify(d.desired)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-emerald-600 text-sm">
                  <Icon name="checkCircle" className="w-4 h-4" />
                  同期済み
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">Shadow データなし</div>
        )}
      </Card>

      {/* 通信履歴 */}
      <Card
        title="通信履歴"
        headerActions={
          <div className="flex items-center gap-2">
            {commTab === 'events' && (
              <select
                value={eventFilter}
                onChange={(e) => setEventFilter(e.target.value)}
                className="text-sm border border-slate-300 rounded px-2 py-1 bg-white"
              >
                <option value="all">すべて</option>
                <option value="connected">接続</option>
                <option value="disconnected">切断</option>
                <option value="shadow_updated">Shadow更新</option>
              </select>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => commTab === 'events' ? fetchEvents(0) : fetchLogs()}
            >
              <Icon name="refresh" className="w-4 h-4" />
            </Button>
          </div>
        }
      >
        {/* タブ */}
        <div className="flex gap-1 mb-4 border-b border-slate-200">
          <button
            onClick={() => setCommTab('events')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              commTab === 'events'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            イベント
          </button>
          <button
            onClick={() => setCommTab('mqtt')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              commTab === 'mqtt'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            MQTTログ
          </button>
          <button
            onClick={() => setCommTab('http')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              commTab === 'http'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            HTTPログ
          </button>
        </div>

        {/* イベントタブ */}
        {commTab === 'events' && (
          <div>
            {eventsLoading && events.length === 0 ? (
              <div className="text-center py-8 text-slate-500">読み込み中...</div>
            ) : events.length === 0 ? (
              <div className="text-center py-8 text-slate-500">イベントがありません</div>
            ) : (
              <>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {events.map((event, idx) => (
                    <EventRow key={`${event.timestamp}-${idx}`} event={event} />
                  ))}
                </div>
                {hasMoreEvents && (
                  <div className="pt-3 mt-3 text-center border-t border-slate-100">
                    <button
                      onClick={() => fetchEvents(events.length)}
                      disabled={eventsLoading}
                      className="px-4 py-2 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg disabled:opacity-50"
                    >
                      {eventsLoading ? '読み込み中...' : 'もっと見る'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* MQTTログタブ */}
        {commTab === 'mqtt' && (
          <div>
            {logsLoading ? (
              <div className="text-center py-8 text-slate-500">読み込み中...</div>
            ) : (() => {
              const mqttLogs = logs.filter(log => log.protocol === 'MQTT');
              return mqttLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  MQTTログがありません（過去2時間）
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {mqttLogs.map((log, idx) => (
                    <LogRow key={`${log.timestamp}-${idx}`} log={log} />
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* HTTPログタブ */}
        {commTab === 'http' && (
          <div>
            {logsLoading ? (
              <div className="text-center py-8 text-slate-500">読み込み中...</div>
            ) : (() => {
              const httpLogs = logs.filter(log => log.protocol === 'HTTP');
              return httpLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  HTTPログがありません（過去2時間）
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {httpLogs.map((log, idx) => (
                    <LogRow key={`${log.timestamp}-${idx}`} log={log} />
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </Card>

      {/* OTA更新モーダル */}
      <Modal
        isOpen={showOtaModal}
        onClose={() => setShowOtaModal(false)}
        title={`OTA更新 - ${thingName}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowOtaModal(false)} disabled={otaStarting}>
              キャンセル
            </Button>
            <Button
              variant="primary"
              onClick={handleStartOta}
              disabled={otaStarting || !selectedFirmware}
            >
              {otaStarting ? (
                <>
                  <Icon name="loader" className="w-4 h-4 animate-spin" />
                  実行中...
                </>
              ) : (
                <>
                  <Icon name="uploadCloud" className="w-4 h-4" />
                  OTA実行
                </>
              )}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* 現在のバージョン */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <span className="text-sm text-slate-600">現在のFWバージョン</span>
            <span className="text-sm font-mono text-slate-900">
              {shadow?.state?.reported?.version || device.firmwareVersion || '不明'}
            </span>
          </div>

          {/* ファームウェア選択 */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              配信するファームウェア（承認済みのみ表示）
            </label>
            {firmwareLoading ? (
              <div className="text-center py-6 text-slate-500">読み込み中...</div>
            ) : firmwareList.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                配信可能なファームウェアがありません。
                <br />
                <span className="text-xs">
                  OTAパイプラインの承認、またはファームウェア画面で released 状態にしてください。
                </span>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {firmwareList.map((fw) => (
                  <label
                    key={fw.version}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedFirmware === fw.version
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="firmware"
                      value={fw.version}
                      checked={selectedFirmware === fw.version}
                      onChange={() => setSelectedFirmware(fw.version)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">{fw.version}</span>
                        <Badge variant={fw.updateType === 'rauc-update' ? 'primary' : 'default'} size="sm">
                          {fw.updateType === 'rauc-update' ? 'RAUCバンドル (OS)' : 'Agent更新'}
                        </Badge>
                      </div>
                      {fw.description && (
                        <p className="text-xs text-slate-500 mt-1 truncate">{fw.description}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                        {fw.fileSize && <span>{(fw.fileSize / 1024 / 1024).toFixed(1)} MB</span>}
                        {fw.uploadedAt && <span>{new Date(fw.uploadedAt).toLocaleString('ja-JP')}</span>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* RAUC更新時の注意事項 */}
          {firmwareList.find((fw) => fw.version === selectedFirmware)?.updateType === 'rauc-update' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="alertTriangle" className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-700 space-y-1">
                  <p className="text-sm font-medium text-amber-800">RAUCバンドル更新の前提条件</p>
                  <p>・FC に OTA 対応ブートローダが書き込み済みであること（未対応だと PX4 書き込み失敗→自動ロールバック）</p>
                  <p>・バンドルに実 PX4 ファームウェアが同梱されていること（ダミーのままだとロールバック）</p>
                  <p>・インストール後にデバイスは自動再起動し、初回起動時に PX4 書き込み（約4分）が実行されます。この間は電源を切らないでください</p>
                  <p>・ジョブが SUCCEEDED でも PX4 書き込み失敗時は旧バージョンへ自動ロールバックされます。再接続後に FW バージョンを確認してください</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Shadow 編集モーダル */}
      {/* 担当者変更モーダル */}
      <Modal
        isOpen={showAssigneeModal}
        onClose={() => setShowAssigneeModal(false)}
        title={`担当者を変更 - ${thingName}`}
        footer={
          <>
            {device.assigneeUserId && (
              <Button variant="danger-outline" onClick={handleClearAssignee} disabled={assigneeSaving}>
                解除
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowAssigneeModal(false)} disabled={assigneeSaving}>
              キャンセル
            </Button>
            {canManageAssignee ? (
              <Button onClick={handleSaveAssignee} loading={assigneeSaving} disabled={!selectedAssignee || assigneeLoading}>
                設定する
              </Button>
            ) : (
              !isSelfAssigned && (
                <Button onClick={() => applyAssignee(profile.userId, profile.orgId)} loading={assigneeSaving}>
                  自分を担当にする
                </Button>
              )
            )}
          </>
        }
      >
        {canManageAssignee ? (
          assigneeLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
              <Icon name="loader" className="w-4 h-4 animate-spin" />
              メンバー一覧を取得中...
            </div>
          ) : (
            <Select
              label="担当者"
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              placeholder="メンバーを選択"
              options={assigneeCandidates.map((c) => ({ value: `${c.orgId}|${c.userId}`, label: c.label }))}
            />
          )
        ) : (
          <p className="text-sm text-slate-600">
            {isSelfAssigned
              ? 'この機体の担当者はあなたです。解除する場合は「解除」を押してください。'
              : 'オペレーターは自分自身のみ担当者に設定できます。'}
          </p>
        )}
        <p className="text-xs text-slate-400 mt-4">
          担当者は機体の現在の責任者です。登録者（自動記録）とは別に管理されます。
        </p>
      </Modal>

      <Modal
        isOpen={showShadowEditor}
        onClose={() => setShowShadowEditor(false)}
        title="Desired State を更新"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowShadowEditor(false)}>
              キャンセル
            </Button>
            <Button onClick={handleShadowUpdate} loading={shadowUpdating}>
              更新する
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-500 mb-4">
          JSON形式で desired state を入力してください
        </p>
        <Textarea
          value={shadowEditValue}
          onChange={(e) => setShadowEditValue(e.target.value)}
          rows={12}
          className="font-mono text-sm"
        />
      </Modal>

      {/* フライトログ */}
      <Card
        title="フライトログ"
        headerActions={
          <Link to="/flight-logs" className="text-sm text-primary-500 hover:underline flex items-center gap-1">
            アップロード・一覧へ
            <Icon name="arrowRight" className="w-4 h-4" />
          </Link>
        }
      >
        <FlightLogTable thingName={thingName} pageSize={10} compact onError={setError} />
      </Card>
    </div>
  );
}

// 情報表示行コンポーネント
function InfoRow({ label, value }) {
  return (
    <div className="flex items-start gap-4">
      <span className="text-sm text-slate-500 w-28 flex-shrink-0">{label}</span>
      <span className="text-sm text-slate-900 flex-1">{value}</span>
    </div>
  );
}

// イベント行コンポーネント
const EVENT_TYPE_CONFIG = {
  connected: { icon: 'wifi', color: 'text-green-600', bgColor: 'bg-green-50', label: '接続' },
  disconnected: { icon: 'wifiOff', color: 'text-red-600', bgColor: 'bg-red-50', label: '切断' },
  shadow_updated: { icon: 'refresh', color: 'text-blue-600', bgColor: 'bg-blue-50', label: 'Shadow更新' },
};

function EventRow({ event }) {
  const config = EVENT_TYPE_CONFIG[event.eventType] || {
    icon: 'info',
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    label: event.eventType,
  };

  const getDetailText = () => {
    if (!event.detail) return null;
    if (event.eventType === 'connected' && event.detail.ipAddress) {
      return event.detail.ipAddress;
    }
    if (event.eventType === 'disconnected' && event.detail.disconnectReason) {
      return event.detail.disconnectReason;
    }
    if (event.eventType === 'shadow_updated') {
      const changes = [];
      if (event.detail.current?.reported) {
        const keys = Object.keys(event.detail.current.reported);
        if (keys.length > 0) changes.push(keys.slice(0, 3).join(', '));
      }
      return changes.length > 0 ? changes.join(' ') : null;
    }
    return null;
  };

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${config.bgColor}`}>
      <Icon name={config.icon} className={`w-4 h-4 flex-shrink-0 ${config.color}`} />
      <div className="flex-1 min-w-0">
        <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
        {getDetailText() && (
          <span className="text-xs text-slate-500 ml-2 truncate">{getDetailText()}</span>
        )}
      </div>
      <span className="text-xs text-slate-400 flex-shrink-0">
        {new Date(event.timestamp).toLocaleString('ja-JP')}
      </span>
    </div>
  );
}

// MQTTログ行コンポーネント
const LOG_EVENT_CONFIG = {
  Connect: { color: 'text-green-600', bgColor: 'bg-green-50' },
  Disconnect: { color: 'text-red-600', bgColor: 'bg-red-50' },
  Subscribe: { color: 'text-blue-600', bgColor: 'bg-blue-50' },
  Unsubscribe: { color: 'text-slate-600', bgColor: 'bg-slate-50' },
  Publish: { color: 'text-purple-600', bgColor: 'bg-purple-50' },
};

function LogRow({ log }) {
  const config = LOG_EVENT_CONFIG[log.eventType] || {
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
  };

  const shortTopic = log.topic
    ? log.topic.replace(/^\$aws\/things\/[^/]+\//, '...')
    : null;

  // 接続情報（IP:Port）
  const connectionInfo = log.sourceIp
    ? log.sourcePort ? `${log.sourceIp}:${log.sourcePort}` : log.sourceIp
    : null;

  // エラー詳細
  const hasError = log.status === 'Failure' && (log.reason || log.details);

  return (
    <div className={`p-3 rounded-lg ${config.bgColor}`}>
      <div className="flex items-center gap-3">
        <span className={`text-sm font-medium w-24 flex-shrink-0 ${config.color}`}>
          {log.eventType}
        </span>
        <div className="flex-1 min-w-0">
          {shortTopic && (
            <span className="text-xs text-slate-600 font-mono truncate block">{shortTopic}</span>
          )}
          <div className="flex items-center gap-2 text-xs text-slate-400">
            {connectionInfo && <span>{connectionInfo}</span>}
            {log.protocol && <span className="px-1 bg-slate-200 rounded">{log.protocol}</span>}
            {log.qos !== null && log.qos !== undefined && (
              <span className="px-1 bg-slate-200 rounded">QoS{log.qos}</span>
            )}
            {log.messageSize && <span>{log.messageSize}B</span>}
          </div>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded ${
          log.status === 'Success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
        }`}>
          {log.status}
        </span>
        <span className="text-xs text-slate-400 flex-shrink-0">
          {new Date(log.timestamp).toLocaleTimeString('ja-JP')}
        </span>
      </div>
      {hasError && (
        <div className="mt-2 ml-24 text-xs">
          {log.reason && (
            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded mr-2">{log.reason}</span>
          )}
          {log.details && (
            <span className="text-slate-600">{log.details}</span>
          )}
        </div>
      )}
    </div>
  );
}
